import { FirstParameter, LastParameter, ParametersWithoutSecondLastAndLast } from "../types/utils";
import API from "./TimeEditAPI";

/**
 * Creates a async promise based function from a callback based API function where it's assumed that
 * the last argument is a callback in the given function
 * @param fn API callback based function
 * @returns async function without callback argument.
 */
function promisifyCallback<T extends (...args) => void>(
    fn: T
): (...args: FirstParameter<T>) => Promise<Parameters<LastParameter<T>>[0]> {
    return (...args: FirstParameter<T>) =>
        new Promise((resolve) => {
            const callbackOverride = (res) => resolve(res);

            fn(...args, callbackOverride);
        });
}

/**
 * Creates a async promise based function from a callback based API function where it's assumed that
 * the two last arguments are: `[...args, callbackFn, errorHandlerFn]`
 */
function promisifyCallbackAndErrorHandler<T extends (...args) => void>(
    fn: T
): (...args: ParametersWithoutSecondLastAndLast<T>) => Promise<Parameters<LastParameter<T>>[0]> {
    return (...args: ParametersWithoutSecondLastAndLast<T>) =>
        new Promise((resolve, reject) => {
            const callbackOverride = (res) => resolve(res);
            const errorHandlerOverride = (res) => reject(res);

            fn(...args, callbackOverride, errorHandlerOverride);
        });
}

/**
 * Async promise based variant of the API. Add new methods from API when needed.
 */
const asyncAPI = {
    okToPublishView: promisifyCallback(API.okToPublishView),
    okToChangeViews: promisifyCallback(API.okToChangeViews),
    partitionObject: promisifyCallbackAndErrorHandler(API.partitionObject),
};

export default asyncAPI;
